/**
 * Permutation & step status helpers — the single rule for counting and labelling
 * test-run results, shared by the live stream, the run details panel, the results
 * summary and the HTML report.
 *
 * ⚠️ THE RULE: count and label permutations by `status`, never by `hasPassed`.
 *
 * A permutation that never actually ran still carries `hasPassed: true`:
 *   • DISABLED — the user explicitly turned this permutation off (disabled: true)
 *   • SKIPPED  — the user did not turn it off, but every step in its Step_Flow was
 *                disabled (or the run was reclaimed), so there was nothing to execute
 *
 * So `permutations.filter(p => p.hasPassed)` reports 4 passed on a run with 2 real
 * passes + 1 skipped + 1 disabled. That was the bug. Use getPermutationStatus().
 *
 * Only PASSED + FAILED actually executed (numberOfPermutationsExecuted).
 *
 * @module shared/utils/permutationStatus
 */

// ── Permutation statuses ────────────────────────────────────────────────

export const PERMUTATION_STATUS = {
  PASSED: 'PASSED',
  FAILED: 'FAILED',
  SKIPPED: 'SKIPPED',
  DISABLED: 'DISABLED',
};

const PERMUTATION_STATUSES = new Set(Object.values(PERMUTATION_STATUS));

// The two statuses that mean "this permutation actually ran".
export const EXECUTED_STATUSES = new Set([PERMUTATION_STATUS.PASSED, PERMUTATION_STATUS.FAILED]);

/**
 * Resolve a permutation's status.
 *
 * `status` from the backend is authoritative. `hasPassed` is only consulted for
 * legacy payloads written before the status field existed — and even then the
 * explicit `disabled` flag is checked first, since a disabled permutation reports
 * hasPassed: true and must never be counted as a pass.
 *
 * @returns {string|null} one of PERMUTATION_STATUS, or null when the permutation
 *   has not finished yet (still running / not started).
 */
export const getPermutationStatus = (perm) => {
  if (!perm) return null;

  const raw = typeof perm.status === 'string' ? perm.status.toUpperCase() : '';
  if (PERMUTATION_STATUSES.has(raw)) return raw;

  // No usable status — fall back, disabled flag first.
  if (perm.disabled === true) return PERMUTATION_STATUS.DISABLED;
  if (perm.hasPassed === false) return PERMUTATION_STATUS.FAILED;
  if (perm.hasPassed === true) return PERMUTATION_STATUS.PASSED;
  return null;
};

export const isPermutationExecuted = (perm) => EXECUTED_STATUSES.has(getPermutationStatus(perm));

/**
 * Tally a permutations array by status.
 * `pending` covers permutations still in flight (no resolved status yet).
 */
export const countPermutationsByStatus = (permutations = []) => {
  const counts = { passed: 0, failed: 0, skipped: 0, disabled: 0, pending: 0 };
  for (const perm of permutations) {
    switch (getPermutationStatus(perm)) {
      case PERMUTATION_STATUS.PASSED:   counts.passed++;   break;
      case PERMUTATION_STATUS.FAILED:   counts.failed++;   break;
      case PERMUTATION_STATUS.SKIPPED:  counts.skipped++;  break;
      case PERMUTATION_STATUS.DISABLED: counts.disabled++; break;
      default:                          counts.pending++;  break;
    }
  }
  return {
    ...counts,
    executed: counts.passed + counts.failed,
    total: permutations.length,
  };
};

/**
 * Resolve the display counts for a run, preferring the backend's authoritative
 * counters and falling back to a status tally of the permutations array.
 *
 * The per-status counters (Passed/Failed/Skipped/Disabled/Executed) are correct in
 * both the stream and the final summary. `numberOfPermutations` (the TOTAL) is NOT:
 * in the stream it currently reports how many have executed so far, so mid-run — and
 * even at COMPLETED — it can read 2 when there are really 4. The total is therefore
 * taken as the largest of the three things that could know it: the permutations
 * array length, the sum of the per-status counters, and the reported total.
 *
 * @param {object|null} testSummary  testResults.testSummary
 * @param {Array}       permutations testResults.permutations
 */
export const resolveRunCounts = (testSummary, permutations = []) => {
  const perms = Array.isArray(permutations) ? permutations : [];
  const tally = countPermutationsByStatus(perms);
  const summary = testSummary || {};

  // A counter is only trusted when the backend actually sent it. Absent → fall back
  // to the tally; present-but-zero is a legitimate value and is kept.
  const pick = (value, fallback) => (Number.isFinite(value) ? value : fallback);

  const passed   = pick(summary.numberOfPermutationsPassed,   tally.passed);
  const failed   = pick(summary.numberOfPermutationsFailed,   tally.failed);
  const skipped  = pick(summary.numberOfPermutationsSkipped,  tally.skipped);
  const disabled = pick(summary.numberOfPermutationsDisabled, tally.disabled);
  const executed = pick(summary.numberOfPermutationsExecuted, passed + failed);

  const total = Math.max(
    perms.length,
    passed + failed + skipped + disabled,
    Number.isFinite(summary.numberOfPermutations) ? summary.numberOfPermutations : 0,
  );

  return {
    total,
    executed,
    passed,
    failed,
    skipped,
    disabled,
    // Not yet finished — only meaningful mid-run.
    pending: Math.max(0, total - (passed + failed + skipped + disabled)),
    // Share of the permutations that RAN and passed. Skipped and disabled ones are
    // excluded from the denominator: they had no opportunity to pass or fail.
    passRate: executed > 0 ? Math.round((passed / executed) * 100) : 0,
  };
};

/**
 * Run-level pass: no permutation FAILED. Mirrors testSummary.hasPassed, which is
 * safe to trust at the RUN level (unlike per-permutation hasPassed).
 */
export const runHasPassed = (testSummary, permutations = []) => {
  if (typeof testSummary?.hasPassed === 'boolean') return testSummary.hasPassed;
  return resolveRunCounts(testSummary, permutations).failed === 0;
};

// ── Run status ──────────────────────────────────────────────────────────

/**
 * A run that was cancelled still reports `runStatus: 'COMPLETED'` — the engine
 * finished tidying up, which is not the same as the test finishing. The truth
 * is in the two fields beside it:
 *
 *   "runStatus": "COMPLETED",
 *   "cancelled": true,
 *   "cancelReason": "Cancelled by user during execution"
 *
 * Rendering `runStatus` directly tells the user a cancelled run completed. So
 * cancellation is resolved here, once, for every surface that shows a status.
 */
export const RUN_STATUS = {
  CANCELLED: 'CANCELLED',
  COMPLETED: 'COMPLETED',
};

/**
 * True when the run was stopped rather than allowed to finish.
 *
 * Only an explicit `cancelled: true` counts. The field is absent on older
 * payloads, and absent is not cancelled.
 */
export const isRunCancelled = (testSummary) => testSummary?.cancelled === true;

/**
 * The status to show a user: `CANCELLED` when it was cancelled, otherwise
 * whatever the engine reported.
 *
 * The counts are deliberately left alone. A run cancelled part-way still has
 * permutations that genuinely passed, and reporting them is correct — what is
 * wrong is calling the run itself complete.
 *
 * @returns {{status: string|null, isCancelled: boolean, cancelReason: string|null}}
 */
export const resolveRunOutcome = (testSummary) => {
  const cancelled = isRunCancelled(testSummary);
  return {
    status: cancelled ? RUN_STATUS.CANCELLED : (testSummary?.runStatus ?? null),
    isCancelled: cancelled,
    cancelReason: cancelled ? (testSummary?.cancelReason || null) : null,
  };
};

// ── Step results ────────────────────────────────────────────────────────

export const STEP_RESULT = {
  PASS: 'PASS',
  FAIL: 'FAIL',
  SKIP: 'SKIP',
  DISABLED: 'DISABLED',
};

const STEP_RESULTS = new Set(Object.values(STEP_RESULT));

/**
 * Resolve a step's result badge.
 *
 * Steps carry a derived `result` (PASS | FAIL | SKIP | DISABLED) — use it, not
 * hasPassed. A DISABLED step (user turned it off) and a SKIP step (an earlier step
 * failed, or an untaken conditional branch) both report hasPassed: true.
 *
 * @returns {string|null} one of STEP_RESULT, or null when the step is still running.
 */
export const getStepResult = (step) => {
  if (!step) return null;

  const raw = typeof step.result === 'string' ? step.result.toUpperCase() : '';
  if (STEP_RESULTS.has(raw)) return raw;

  // Legacy payloads with no `result` field.
  if (step.conditionSkipped) return STEP_RESULT.SKIP;
  if (!step.endDateTime) return null;              // still in flight
  return step.hasPassed === false ? STEP_RESULT.FAIL : STEP_RESULT.PASS;
};

/** A step that did not run — neither a pass nor a failure. */
export const isStepInert = (step) => {
  const result = getStepResult(step);
  return result === STEP_RESULT.SKIP || result === STEP_RESULT.DISABLED;
};

/** Tally steps by result — used for the "N actions, N passed" group headers. */
export const countStepsByResult = (steps = []) => {
  const counts = { pass: 0, fail: 0, skip: 0, disabled: 0, pending: 0 };
  for (const step of steps) {
    switch (getStepResult(step)) {
      case STEP_RESULT.PASS:     counts.pass++;     break;
      case STEP_RESULT.FAIL:     counts.fail++;     break;
      case STEP_RESULT.SKIP:     counts.skip++;     break;
      case STEP_RESULT.DISABLED: counts.disabled++; break;
      default:                   counts.pending++;  break;
    }
  }
  return counts;
};
